import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';
import { Shell, formatLongDate } from './PublicSitePage';
import { useRsvpHousehold, useSubmitRsvp } from './queries';

/**
 * One household's reply, opened from the personal link in their invitation.
 *
 * The token in the URL is the whole of the authentication, which is the right
 * trade for a wedding — asking a 70-year-old aunt to create an account to say
 * she is coming is how you end up collecting RSVPs by text message anyway.
 *
 * Nobody is pre-ticked. An unanswered person shows neither yes nor no, because a
 * form that defaults to "coming" collects people who are not, and one that
 * defaults to "not coming" is worse.
 */

type Answer = { attending: boolean | null; mealChoice: string; dietaryNotes: string };

export function RsvpPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const household = useRsvpHousehold(token);
  const submit = useSubmitRsvp(token);
  const locale = household.data?.locale;

  // The couple's language, not the guest's phone's — same rule as the page they
  // came from.
  useEffect(() => {
    if (locale && i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [locale]);

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [songRequest, setSongRequest] = useState('');
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);

  // Seeded from the server once it arrives, so reopening the link shows what you
  // said last time rather than an empty form that looks like it was never sent.
  useEffect(() => {
    if (!household.data) return;
    setAnswers(
      Object.fromEntries(
        household.data.members.map((m) => [
          m.guestId,
          {
            attending: m.attending,
            mealChoice: m.mealChoice ?? '',
            dietaryNotes: m.dietaryNotes ?? '',
          },
        ]),
      ),
    );
    setSongRequest(household.data.songRequest ?? '');
  }, [household.data]);

  if (household.isLoading) return <Shell>{null}</Shell>;

  if (household.isError || !household.data) {
    return (
      <Shell>
        <h1 className="font-display text-display">{t('public.invitationNotFound')}</h1>
        <p className="mt-4 text-body text-leaf-soft">{t('public.invitationNotFoundBody')}</p>
      </Shell>
    );
  }

  const data = household.data;

  if (done) {
    return (
      <Shell>
        <h1 className="font-display text-display">{t('public.thankYou')}</h1>
        <p className="mt-4 text-body text-leaf-soft">{t('public.replySaved')}</p>
      </Shell>
    );
  }

  const unanswered = data.members.filter((m) => answers[m.guestId]?.attending == null).length;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload = data.members
      .map((m) => ({ guestId: m.guestId, ...answers[m.guestId] }))
      .filter((a): a is { guestId: string } & Answer => a.attending != null)
      .map((a) => ({
        guestId: a.guestId,
        attending: a.attending as boolean,
        ...(a.mealChoice ? { mealChoice: a.mealChoice } : {}),
        ...(a.dietaryNotes ? { dietaryNotes: a.dietaryNotes } : {}),
      }));
    if (payload.length === 0) return;

    submit.mutate(
      {
        answers: payload,
        ...(songRequest.trim() ? { songRequest: songRequest.trim() } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      },
      { onSuccess: () => setDone(true) },
    );
  };

  return (
    <Shell>
      <header className="flex flex-col gap-2 border-b border-line pb-8">
        <p className="eyebrow">{data.title}</p>
        <h1 className="font-display text-[36px] font-light leading-tight">{data.householdLabel}</h1>
        {data.date && (
          <p className="tabular text-body text-leaf-soft">{formatLongDate(data.date)}</p>
        )}
        {data.rsvpNote && <p className="mt-2 text-body text-leaf-soft">{data.rsvpNote}</p>}
      </header>

      {data.deadlinePassed ? (
        <p className="py-12 text-body text-leaf-soft">{t('public.repliesClosedGuest')}</p>
      ) : (
        <form className="flex flex-col gap-10 py-10" onSubmit={onSubmit}>
          <ul className="flex flex-col gap-8">
            {data.members.map((member) => {
              const answer = answers[member.guestId] ?? {
                attending: null,
                mealChoice: '',
                dietaryNotes: '',
              };
              const set = (patch: Partial<Answer>) =>
                setAnswers((prev) => ({
                  ...prev,
                  [member.guestId]: { ...answer, ...patch },
                }));

              return (
                <li key={member.guestId} className="flex flex-col gap-3">
                  <span className="text-section">{member.name}</span>

                  <div className="flex gap-2">
                    {/* Two explicit buttons rather than a checkbox: "not ticked"
                        and "not coming" must not be the same thing. */}
                    <Choice
                      selected={answer.attending === true}
                      onSelect={() => set({ attending: true })}
                      label={t('public.coming')}
                    />
                    <Choice
                      selected={answer.attending === false}
                      onSelect={() => set({ attending: false, mealChoice: '', dietaryNotes: '' })}
                      label={t('public.cannotCome')}
                    />
                  </div>

                  {answer.attending === true && data.mealChoices.length > 0 && (
                    <label className="flex flex-col gap-1">
                      <span className="eyebrow">{t('public.meal')}</span>
                      <select
                        value={answer.mealChoice}
                        onChange={(e) => set({ mealChoice: e.target.value })}
                        className="min-h-tap w-full rounded border border-line bg-paper px-3 text-body"
                      >
                        <option value="">{t('public.noPreference')}</option>
                        {data.mealChoices.map((choice) => (
                          <option key={choice} value={choice}>
                            {choice}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {answer.attending === true && (
                    <label className="flex flex-col gap-1">
                      <span className="eyebrow">{t('public.anythingToKnow')}</span>
                      <TextInput
                        placeholder={t('public.anythingToKnowHint')}
                        value={answer.dietaryNotes}
                        onChange={(e) => set({ dietaryNotes: e.target.value })}
                      />
                    </label>
                  )}
                </li>
              );
            })}
          </ul>

          {data.songRequests && (
            <label className="flex flex-col gap-1">
              <span className="eyebrow">{t('public.songRequest')}</span>
              <TextInput value={songRequest} onChange={(e) => setSongRequest(e.target.value)} />
            </label>
          )}

          <label className="flex flex-col gap-1">
            <span className="eyebrow">{t('public.noteForCouple')}</span>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded border border-line bg-paper px-3 py-2 text-body"
            />
          </label>

          {submit.isError && (
            <p role="alert" className="text-caption text-status-declinedFg">
              {t('public.didNotSave')}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Button type="submit" loading={submit.isPending}>
              {t('public.sendReply')}
            </Button>
            {unanswered > 0 && (
              <p className="text-caption text-leaf-soft">
                {unanswered === data.members.length
                  ? t('public.answerAtLeastOne')
                  : t('public.stillToAnswer', { count: unanswered })}
              </p>
            )}
          </div>
        </form>
      )}
    </Shell>
  );
}

function Choice({
  selected,
  onSelect,
  label,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`min-h-tap flex-1 rounded-pill border px-4 text-body transition-colors ${
        selected
          ? 'border-leaf bg-leaf text-cream'
          : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft'
      }`}
    >
      {label}
    </button>
  );
}
